import { Card, Grid, Typography, CircularProgress, Box} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getLeaveCount } from "src/services/leaveService";
import { RootStateType } from 'src/store/RootReducer';

const CircularProgressWithLabel = ({
  color,
  total,
  available,
  isNoPay
}) => {
  const centerValue = isNoPay ? total : available;
  const progressValue =
    total > 0 ? (isNoPay ? 100 : (available / total) * 100) : 0;
  const showEmptyCircle = !isNoPay && available === 0 && total === 0;

  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress
        variant="determinate"
        value={100}
        size={48}
        thickness={5}
        sx={{
          color: '#e0e0e0',
          position: 'absolute',
          svg: {
            strokeLinecap: "round",
          },
        }}
      />
      <CircularProgress
        variant="determinate"
        value={progressValue}
        size={48}
        thickness={5}
        sx={{
          color: showEmptyCircle ? '#e0e0e0' : color,
          svg: {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{
          transform: "translate(-50%, -50%)",
          fontSize: "16px",
          fontWeight: "bold",
        }}
      >
        {centerValue}
      </Box>
    </Box>
  );
};
export { CircularProgressWithLabel };

const LeaveWidget = () => {
  const [leaveData, setLeaveData] = useState([]);
  const employeeId = useSelector(
    (state: RootStateType) => state.appReducer?.user?.EmployeeId
  );

  useEffect(() => {
    const fetchLeaveData = async () => {
      try {
        const response = await getLeaveCount(employeeId);
        if (response && response.data && Array.isArray(response.data.data)) {
          const mappedData = response.data.data.map((leave) => {
            const isNoPay = leave.leaveType === 'No Pay Leave';
            const taken = leave.leavesTaken ?? 0;
            if (isNoPay) {
              return {
                type: leave.leaveType,
                used: taken,
                total: taken,
                isNoPay: true,
                color: getColorForLeaveType(leave.leaveType)
              };
            }
            return {
              type: leave.leaveType,
              used: taken,
              total: leave.leavesAvailable,
              isNoPay: false,
              color: getColorForLeaveType(leave.leaveType)
            };
          });
          setLeaveData(mappedData);
          localStorage.setItem("leaveData", JSON.stringify(mappedData));
        } else {
          toast.error("Error fetching leave data. Please try again later.");
          setLeaveData([]);
        }
      } catch (error) {
        toast.error("Error fetching leave data. Please try again later.");
      }
    };

    fetchLeaveData();
  }, [employeeId]);

  const getColorForLeaveType = (leaveType) => {
    const colors = {
      "Annual Leave": "#FF8A65",
      "Lieu Leave": "#64B5F6",
      "Casual Leave": "#BA68C8",
      "Short Leave": "#EF5350",
      "Carry Forward Leave": "#FFD54F",
      "No Pay Leave": "#90A4AE"
    };
    return colors[leaveType] || "#90A4AE"; 
  };

  const sortedLeaveData = [...leaveData].sort((a, b) =>
    a.type.localeCompare(b.type)
  );

  return (
    <Card style={{ padding: "16px" ,boxShadow: '0 2px 5px rgba(0,0,0,0.1)'}}>
      <div
        className="widget-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          borderBottom: "1px solid #e0e0e0",
          paddingBottom: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <DescriptionIcon
            sx={{
              alignSelf: "center",
              fontSize: "19px",
              marginRight: "6px",
            }}
          />
          <Typography variant="h6" fontWeight="bold">
            My Leave Summary
          </Typography>
        </div>
      </div>
      <Grid container spacing={2}>
        {sortedLeaveData.map((leave, index) => (
          <Grid item xs={6} key={index}>
            <Card
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px",
                borderRadius: "12px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
              }}
            >
              <CircularProgressWithLabel
                color={leave.color}
                total={leave.total}
                available={leave.isNoPay ? leave.used : leave.total - leave.used}
                isNoPay={leave.isNoPay}
              />
              <div style={{ marginLeft: "10px" }}>
                <Typography fontWeight="bold">{leave.type}</Typography>
                <Typography variant="caption">
                  {leave.isNoPay
                    ? `${leave.used}/${leave.used}`
                    : `${leave.used}/${leave.total}`}
                </Typography>
              </div>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

export default LeaveWidget;